import React from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  useTheme,
  Button,
  CircularProgress,
} from '@mui/material';

import {
  GitBranchIcon,
  ArrowRightIcon,
  PlayIcon,
  FileTextIcon,
  WarningIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../utils/redux/redux';
import {
  setCurrentFlow,
  setIsExecuting,
  setExecutionResult,
  setError,
  validateFormData,
} from '../../utils/redux/slices/windmillFlowSlice';
import { runWindmillWorkflowApi } from '../../utils/apis';
import { useQueryClient } from '@tanstack/react-query';

export interface FlowNodeData {
  label: string;
  type:
    | 'script'
    | 'branchone'
    | 'rawscript'
    | 'error_handler'
    | 'trigger'
    | 'branchall';
  path?: string;
  input_transforms?: Record<string, any>;
  content?: string;
  language?: string;
  status?: string | undefined;
  isTrigger?: boolean;
  expression?: string;
  summary?: string;
  tag?: string;
  icon?: string;
  onNodeClick?: (nodeId: string, data: FlowNodeData) => void;
  // Add flowData property
  flowData?: any;
}

// Utility function for Script node status colors
const getScriptNodeStatusColors = (status: string, theme: any) => {
  switch (status) {
    case 'success':
    case 'completed':
      return {
        bgColor: '#C1FFD8',
        textColor: '#000000',
        borderColor: theme.palette.success.dark,
      };
    case 'failure':
    case 'failed':
      return {
        bgColor: '#F77171',
        textColor: '#000000',
        borderColor: theme.palette.error.dark,
      };
    case 'running':
      return {
        bgColor: theme.palette.info.main,
        textColor: '#ffffff',
        borderColor: theme.palette.info.dark,
      };
    case 'waiting':
    case 'pending':
    case 'waitingForPriorSteps':
      return {
        bgColor: theme.palette.warning.main,
        textColor: '#ffffff',
        borderColor: theme.palette.warning.dark,
      };
    default:
      return {
        bgColor: '#ffffff',
        textColor: theme.palette.text.primary,
        borderColor: theme.palette.grey[300],
      };
  }
};

// Utility function for all other node status colors
const getNodeStatusColors = (status: string, theme: any) => {
  const defaultBg = '#D5DEE8';
  const defaultText = theme.palette.text.primary;

  switch (status) {
    case 'success':
    case 'completed':
      return {
        bgColor: defaultBg,
        textColor: defaultText,
        borderColor: theme.palette.success.main,
      };
    case 'failure':
    case 'failed':
      return {
        bgColor: defaultBg,
        textColor: defaultText,
        borderColor: theme.palette.error.main,
      };
    case 'running':
      return {
        bgColor: defaultBg,
        textColor: defaultText,
        borderColor: theme.palette.info.main,
      };
    case 'waiting':
    case 'pending':
    case 'waitingForPriorSteps':
      return {
        bgColor: defaultBg,
        textColor: defaultText,
        borderColor: theme.palette.warning.main,
      };
    default:
      return {
        bgColor: defaultBg,
        textColor: defaultText,
        borderColor: theme.palette.grey[300],
      };
  }
};

// Script Node (for processing steps)
export const ScriptNode: React.FC<NodeProps> = ({
  data,
  selected,
  id,
}: any) => {
  const theme = useTheme();

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  const statusColors = getScriptNodeStatusColors(
    data.status || 'default',
    theme
  );
  const hasStatus = data.status && data.status !== 'default';

  return (
    <>
      {/* Top Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      {/* Bottom Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          bottom: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 200,
          minHeight: 50,
          maxWidth: 400,
          maxHeight: 150,
          width: '100%',
          height: '100%',
          backgroundColor: statusColors.bgColor,
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${statusColors.borderColor}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              mr: 1,
              color: statusColors.textColor,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              opacity: hasStatus ? 1 : 0.7,
            }}
          >
            <FileTextIcon size={16} />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Path - ellipsed */}
            <Typography
              variant="body2"
              sx={{
                color: statusColors.textColor,
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.25,
                opacity: hasStatus ? 0.9 : 0.7,
              }}
            >
              {data.path || 'Script'}
            </Typography>

            {/* Script name */}
            <Typography
              variant="body2"
              sx={{
                color: statusColors.textColor,
                fontWeight: 500,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {data.path ? data.path.split('/').pop() : data.label}
            </Typography>
          </Box>

          {/* Status chip - only if not default */}
          {hasStatus && (
            <Chip
              label={data.status}
              size="small"
              sx={{
                ml: 1,
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#000000',
                display: 'flex',
                alignItems: 'center',
                '& .MuiChip-label': {
                  px: 0.75,
                  py: 0,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center', // Add this for horizontal centering
                },
              }}
            />
          )}
        </Box>
      </Paper>
    </>
  );
};

// Condition Node (for branching logic)
export const ConditionNode: React.FC<NodeProps> = ({
  data,
  selected,
  id,
}: any) => {
  const theme = useTheme();

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  const statusColors = getScriptNodeStatusColors(
    data.status || 'default',
    theme
  );
  const hasStatus = data.status && data.status !== 'default';

  return (
    <>
      {/* Top Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      {/* Bottom Handle */}
      {data.handles?.map((handle: { id: string; label: string }) => (
        <Handle
          key={handle.id}
          type="source"
          position={Position.Bottom}
          id={handle.id}
          style={{
            width: 10,
            height: 10,
            backgroundColor: theme.palette.lightGray.main,
            border: `1px solid ${theme.palette.primary.main}`,
            bottom: -5,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}
        />
      ))}

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 200,
          minHeight: 50,
          maxWidth: 400,
          maxHeight: 150,
          width: '100%',
          height: '100%',
          backgroundColor: statusColors.bgColor,
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${statusColors.borderColor}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              mr: 1,
              color: statusColors.textColor,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              opacity: hasStatus ? 1 : 0.7,
            }}
          >
            <GitBranchIcon size={16} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: statusColors.textColor,
                fontWeight: 500,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Conditional Branch
            </Typography>
          </Box>

          {/* Status chip - only if not default */}
          {hasStatus && (
            <Chip
              label={data.status}
              size="small"
              sx={{
                ml: 1,
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#000000',
                display: 'flex',
                alignItems: 'center',
                '& .MuiChip-label': {
                  px: 0.75,
                  py: 0,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              }}
            />
          )}
        </Box>
      </Paper>
    </>
  );
};

// Branch Node (for specific conditions)
export const BranchNode: React.FC<NodeProps> = ({
  data,
  selected,
  id,
}: any) => {
  const theme = useTheme();

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  const statusColors = getNodeStatusColors(data.status || 'default', theme);

  return (
    <>
      {/* Top Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      {/* Bottom Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          bottom: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 200,
          minHeight: 50,
          maxWidth: 400,
          maxHeight: 150,
          width: '100%',
          height: '100%',
          backgroundColor: statusColors.bgColor,
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${statusColors.borderColor}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              mr: 1,
              color: statusColors.textColor,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <ArrowRightIcon size={16} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: statusColors.textColor,
                fontWeight: 500,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: data.expression ? 0.25 : 0,
              }}
            >
              {data.label}
            </Typography>

            {data.expression && (
              <Typography
                variant="body2"
                sx={{
                  color: statusColors.textColor,
                  fontSize: '0.7rem',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity: 0.8,
                }}
              >
                {data.expression}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </>
  );
};

// Default Node
export const DefaultNode: React.FC<NodeProps> = ({
  data,
  selected,
  id,
}: any) => {
  const theme = useTheme();

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  const statusColors = getNodeStatusColors(data.status || 'default', theme);

  return (
    <>
      {/* Top Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      {/* Bottom Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          bottom: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 200,
          minHeight: 50,
          maxWidth: 400,
          maxHeight: 150,
          width: '100%',
          height: '100%',
          backgroundColor: statusColors.bgColor,
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${statusColors.borderColor}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              mr: 1,
              color: statusColors.textColor,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <PlayIcon size={16} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: statusColors.textColor,
                fontWeight: 500,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: data.summary ? 0.25 : 0,
              }}
            >
              {data.label}
            </Typography>

            {data.summary && (
              <Typography
                variant="body2"
                sx={{
                  color: statusColors.textColor,
                  fontSize: '0.7rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity: 0.8,
                }}
              >
                {data.summary}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </>
  );
};

// Input Node
export const InputNode: React.FC<NodeProps> = ({ data, selected, id }: any) => {
  const theme = useTheme();

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  return (
    <>
      {/* Top Handle */}
      {!data.isJobDetail && (
        <Handle
          type="target"
          position={Position.Top}
          style={{
            width: 10,
            height: 10,
            backgroundColor: theme.palette.lightGray.main,
            border: `1px solid ${theme.palette.primary.main}`,
            top: -5,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}
        />
      )}

      {/* Bottom Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          bottom: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 150,
          minHeight: 50,
          maxWidth: 300,
          maxHeight: 100,
          width: '100%',
          height: '100%',
          backgroundColor: data.isJobDetail ? '#D5DEE8' : '#ffffff',
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              color: theme.palette.text.secondary,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowRightIcon size={16} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              fontSize: '0.8rem',
            }}
          >
            Input
          </Typography>
        </Box>
      </Paper>
    </>
  );
};

// Result Node
export const ResultNode: React.FC<NodeProps> = ({
  data,
  selected,
  id,
}: any) => {
  const theme = useTheme();

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  const statusColors = getScriptNodeStatusColors(
    data.status || 'default',
    theme
  );

  return (
    <>
      {/* Top Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 150,
          minHeight: 50,
          maxWidth: 300,
          maxHeight: 100,
          width: '100%',
          height: '100%',
          backgroundColor: statusColors.bgColor,
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${statusColors.borderColor}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              color: statusColors.textColor,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CheckCircleIcon size={16} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: statusColors.textColor,
              fontWeight: 600,
              fontSize: '0.8rem',
            }}
          >
            Result
          </Typography>
        </Box>
      </Paper>
    </>
  );
};

// Error Handler Node
export const ErrorHandlerNode: React.FC<NodeProps> = ({
  data,
  selected,
  id,
}: any) => {
  const theme = useTheme();

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  const statusColors = getScriptNodeStatusColors(
    data.status || 'default',
    theme
  );
  const hasStatus = data.status && data.status !== 'default';

  return (
    <>
      {/* Top Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 200,
          minHeight: 50,
          maxWidth: 400,
          maxHeight: 150,
          width: '100%',
          height: '100%',
          backgroundColor: statusColors.bgColor,
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${statusColors.borderColor}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              mr: 1,
              color: theme.palette.error.main,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <WarningIcon size={16} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: statusColors.textColor,
                fontWeight: 500,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: data.language || data.job || data.parentModule ? 0.25 : 0,
              }}
            >
              {data.label || 'Error Handler'}
            </Typography>

            {/* Show additional info in compact format */}
            {(data.language || data.job || data.parentModule) && (
              <Typography
                variant="body2"
                sx={{
                  color: statusColors.textColor,
                  fontSize: '0.7rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity: 0.7,
                }}
              >
                {data.language && `Language: ${data.language}`}
                {data.job && `Job: ${data.job.substring(0, 8)}...`}
                {data.parentModule && `Triggered by: ${data.parentModule}`}
              </Typography>
            )}
          </Box>

          {/* Status chip - only if not default */}
          {hasStatus && (
            <Chip
              label={data.status}
              size="small"
              sx={{
                ml: 1,
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#000000',
                display: 'flex',
                alignItems: 'center',
                '& .MuiChip-label': {
                  px: 0.75,
                  py: 0,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              }}
            />
          )}
        </Box>
      </Paper>
    </>
  );
};

// Convergence Node
export const ConvergenceNode: React.FC<NodeProps> = ({
  data,
  selected,
  id,
}: any) => {
  const theme = useTheme();

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  const statusColors = getNodeStatusColors(data.status || 'default', theme);

  return (
    <>
      {/* Top Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      {/* Bottom Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          bottom: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 200,
          minHeight: 50,
          maxWidth: 400,
          maxHeight: 150,
          width: '100%',
          height: '100%',
          backgroundColor: statusColors.bgColor,
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${statusColors.borderColor}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              mr: 1,
              color: statusColors.textColor,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <ArrowRightIcon size={16} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: statusColors.textColor,
                fontWeight: 500,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: data.summary ? 0.25 : 0,
              }}
            >
              {data.label || 'Convergence'}
            </Typography>

            {data.summary && (
              <Typography
                variant="body2"
                sx={{
                  color: statusColors.textColor,
                  fontSize: '0.7rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity: 0.8,
                }}
              >
                {data.summary}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </>
  );
};

// Parallel Node (for parallel branching logic)
export const ParallelNode: React.FC<NodeProps> = ({
  data,
  selected,
  id,
}: any) => {
  const theme = useTheme();

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  const statusColors = getScriptNodeStatusColors(
    data.status || 'default',
    theme
  );
  const hasStatus = data.status && data.status !== 'default';

  return (
    <>
      {/* Top Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      {/* Bottom Handle */}
      {data.handles?.map((handle: { id: string; label: string }) => (
        <Handle
          key={handle.id}
          type="source"
          position={Position.Bottom}
          id={handle.id}
          style={{
            width: 10,
            height: 10,
            backgroundColor: theme.palette.lightGray.main,
            border: `1px solid ${theme.palette.primary.main}`,
            bottom: -5,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}
        />
      ))}

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 200,
          minHeight: 50,
          maxWidth: 400,
          maxHeight: 150,
          width: '100%',
          height: '100%',
          backgroundColor: statusColors.bgColor,
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${statusColors.borderColor}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Git branch icon for parallel execution */}
          <Box
            sx={{
              mr: 1,
              color: statusColors.textColor,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              opacity: hasStatus ? 1 : 0.7,
            }}
          >
            <GitBranchIcon size={16} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: statusColors.textColor,
                fontWeight: 500,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Parallel Branch
            </Typography>
          </Box>

          {/* Status chip - only if not default */}
          {hasStatus && (
            <Chip
              label={data.status}
              size="small"
              sx={{
                ml: 1,
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#000000',
                display: 'flex',
                alignItems: 'center',
                '& .MuiChip-label': {
                  px: 0.75,
                  py: 0,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              }}
            />
          )}
        </Box>
      </Paper>
    </>
  );
};

// Trigger Node
export const TriggerNode: React.FC<NodeProps> = ({
  data,
  selected,
  id,
}: any) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Get execution state and validation from Redux
  const {
    isExecuting,
    executionResult,
    error,
    // currentFlow,
    formData,
    validationErrors,
  } = useSelector((state: RootState) => state.windmillFlow);

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(id, data);
    }
  };

  // Function to find and trigger input node click
  const triggerInputNodeClick = () => {
    // Find the input node in the current flow
    const inputNode = {
      id: 'input-node',
      type: 'script',
      label: 'Input',
      status: 'completed',
      flowData: data.flowData,
    };

    // Trigger the input node click to open the sidebar
    if (data.onNodeClick) {
      data.onNodeClick('input-node', inputNode);
    }
  };

  const handleExecuteWorkflow = async () => {
    if (!data.flowData) return;

    // Check if form is valid before executing
    if (data.flowData.schema) {
      const { isValid } = validateFormData(formData, data.flowData.schema);
      if (!isValid) {
        // Instead of showing error, open the input node sidebar
        triggerInputNodeClick();
        return;
      }
    }

    dispatch(setCurrentFlow(data.flowData));
    dispatch(setIsExecuting(true));
    dispatch(setError(null));

    try {
      const result = await runWindmillWorkflowApi(data.flowData.path, formData);
      dispatch(setExecutionResult(result));
      // Invalidate all windmillJobs queries to ensure execution history updates
      queryClient.invalidateQueries({
        queryKey: ['windmillJobs'],
        exact: false,
      });
    } catch (err: any) {
      dispatch(
        setError(err?.response?.data?.message || 'Failed to execute workflow')
      );
    } finally {
      dispatch(setIsExecuting(false));
    }
  };

  // Check if required fields are missing
  const hasRequiredFields = data.flowData?.schema?.required?.length > 0;
  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const isMissingRequiredFields = hasRequiredFields && hasValidationErrors;

  return (
    <>
      {/* Bottom Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: 10,
          height: 10,
          backgroundColor: theme.palette.lightGray.main,
          border: `1px solid ${theme.palette.primary.main}`,
          bottom: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      />

      <Paper
        elevation={selected ? 3 : 1}
        onClick={handleNodeClick}
        sx={{
          minWidth: 200,
          minHeight: 80,
          maxWidth: 400,
          maxHeight: 200,
          width: '100%',
          height: '100%',
          backgroundColor: '#ffffff',
          border: selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Box
              sx={{
                mr: 1,
                color: theme.palette.text.secondary,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <PlayIcon size={16} />
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {data.label}
            </Typography>
          </Box>

          {/* Execute Workflow Button */}
          {data.flowData && (
            <Box sx={{ mt: 'auto' }}>
              <Button
                variant="contained"
                size="small"
                onClick={e => {
                  e.stopPropagation(); // Prevent node click
                  handleExecuteWorkflow();
                }}
                disabled={isExecuting}
                startIcon={
                  isExecuting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PlayIcon size={16} />
                  )
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  width: '100%',
                  fontSize: '0.75rem',
                  height: 32,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {isExecuting
                  ? 'Executing...'
                  : isMissingRequiredFields
                    ? 'Configure & Execute'
                    : 'Execute Workflow'}
              </Button>

              {/* Show status messages */}
              {isMissingRequiredFields && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.info.main,
                    fontSize: '0.65rem',
                    display: 'block',
                    mt: 0.5,
                    textAlign: 'center',
                  }}
                >
                  Click to configure required parameters
                </Typography>
              )}

              {error && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: '0.65rem',
                    display: 'block',
                    mt: 0.5,
                    textAlign: 'center',
                  }}
                >
                  {error}
                </Typography>
              )}

              {executionResult && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.success.main,
                    fontSize: '0.65rem',
                    display: 'block',
                    mt: 0.5,
                    textAlign: 'center',
                  }}
                >
                  Workflow executed successfully!
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </>
  );
};

// Utility function to get node types for React Flow
export const getNodeTypes = () => ({
  scriptNode: ScriptNode,
  conditionNode: ConditionNode,
  parallelNode: ParallelNode,
  branchNode: BranchNode,
  defaultNode: DefaultNode,
  errorHandlerNode: ErrorHandlerNode,
  triggerNode: TriggerNode,
  inputNode: InputNode,
  resultNode: ResultNode,
  convergenceNode: ConvergenceNode,
});
